
export function renderAiopsCorrelation () {
      const cardColorMap = {
    "netraa-cyan": {
      badge: "bg-cyan-500/10 border-cyan-500/30",
      icon: "text-cyan-400",
    },
    "netraa-purple": {
      badge: "bg-purple-500/10 border-purple-500/30",
      icon: "text-purple-400",
    },
    "netraa-orange": {
      badge: "bg-orange-500/10 border-orange-500/30",
      icon: "text-orange-400",
    },
  };

  return (
    <div className="transition-opacity duration-300">
      <main className="py-10" id="main-content">
        <div className="relative overflow-x-hidden">
          {/* Glow blobs: hidden on mobile to prevent horizontal scroll */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 hidden md:block w-[800px] h-[600px] bg-netraa-purple/10 rounded-full blur-[120px] pointer-events-none -z-10" />
          <div className="absolute top-40 right-0 hidden md:block w-[600px] h-[500px] bg-netraa-cyan/5 rounded-full blur-[100px] pointer-events-none -z-10" />

          <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-6 font-mono px-4 sm:px-0">
            <span>Platform</span>
            <span className="material-symbols-outlined text-[10px]">chevron_right</span>
            <span>Netraa AIOps</span>
            <span className="material-symbols-outlined text-[10px]">chevron_right</span>
            <span className="text-primary font-bold">Universal Event Correlation</span>
          </div>

          <section className="mb-24 relative px-4 sm:px-0" id="feature-overview">
            <div className="mb-12">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600 leading-tight">
                Universal Event Correlation:<br />
                <span className="text-black dark:text-white">The End of Data Silos</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-text-secondary mx-auto leading-relaxed">
                Break down walls across your enterprise stack. Netraa seamlessly ingests, normalizes, and correlates alerts from heterogeneous environments into a single, unified operational timeline.
              </p>
            </div>

            <div className="glass-card w-full h-[420px] sm:h-[450px] relative flex items-center justify-center overflow-hidden bg-[#030303] border-border-dark">
              <div
                className="absolute inset-0 opacity-10 pointer-events-none"
                style={{
                  backgroundImage: "radial-gradient(#333 1px, transparent 1px)",
                  backgroundSize: "30px 30px",
                }}
              />

              <div className="relative z-20 flex items-center justify-center w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-[#0F1012] to-[#000] border border-white/10 shadow-[0_0_60px_rgba(124,77,255,0.4)] animate-pulse-slow">
                <div className="absolute inset-0 rounded-full border border-netraa-purple/50 animate-ping opacity-20" />
                <span className="text-white font-bold tracking-widest text-[10px] sm:text-xs z-10 text-center leading-tight">
                  NETRAA<br />CORE
                </span>

                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] sm:w-[1200px] h-[160px] sm:h-[200px] bg-gradient-to-b from-transparent via-netraa-purple/10 to-transparent rotate-90 blur-xl pointer-events-none" />
              </div>

              <div className="absolute inset-0 animate-[spin_60s_linear_infinite] z-10 pointer-events-none">
                <div className="orbit-icon top-[15%] left-[25%] border-purple-500/30 text-purple-400">DD</div>
                <div className="absolute top-[15%] left-[25%] w-[50%] h-[50%] border-l border-b border-purple-500/20 rounded-full transparent transform rotate-45" />
                <div className="orbit-icon top-[20%] right-[20%] border-green-500/30 text-green-400">SP</div>
                <div className="orbit-icon bottom-[25%] left-[20%] border-blue-500/30 text-blue-400">DT</div>
                <div className="orbit-icon bottom-[15%] right-[30%] border-yellow-500/30 text-yellow-400">AWS</div>
                <div className="orbit-icon top-[50%] left-[10%] border-orange-500/30 text-orange-400">PROM</div>
                <div className="orbit-icon top-[10%] left-[50%] border-red-500/30 text-red-400">ZBX</div>
              </div>

              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-[20%] left-[28%] w-[22%] h-[1px] bg-gradient-to-r from-purple-500/0 via-purple-500/50 to-netraa-purple transform rotate-[25deg]" />
                <div className="absolute bottom-[28%] left-[23%] w-[27%] h-[1px] bg-gradient-to-r from-blue-500/0 via-blue-500/50 to-netraa-purple transform -rotate-[20deg]" />
                <div className="absolute top-[25%] right-[23%] w-[27%] h-[1px] bg-gradient-to-l from-green-500/0 via-green-500/50 to-netraa-purple transform -rotate-[25deg]" />
                <div className="absolute bottom-[18%] right-[33%] w-[17%] h-[1px] bg-gradient-to-l from-yellow-500/0 via-yellow-500/50 to-netraa-purple transform rotate-[35deg]" />
              </div>
            </div>
          </section>

          <section className="mb-24 scroll-mt-24 px-4 sm:px-0" id="the-problem">
            <div className="mb-16">
              <h2 className="text-2xl sm:text-3xl font-bold flex items-center gap-3 text-grey-500 dark:text-white leading-tight">
                <span className="material-symbols-outlined text-orange-500 text-4xl">
                  warning
                </span>
                <span>
                  The Problem:{" "}
                  <span className="text-grey-600 dark:text-text-secondary">
                    Fragmented Visibility &amp; Alert Noise
                  </span>
                </span>
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div className="max-w-xl">
                <p className="text-base sm:text-lg text-grey-600 dark:text-text-secondary leading-relaxed mb-6">
                  Your tools don't talk to each other. Datadog shouts, Splunk whispers, and Dynatrace speaks a different language.
                </p>

                <p className="text-base sm:text-lg text-grey-600 dark:text-text-secondary leading-relaxed border-l-2 border-netraa-orange pl-4">
                  The result?{" "}
                  <span className="text-grey-900 dark:text-white font-medium">
                    Swivel-chair operations
                  </span>
                  , critical blind spots, and endless alert fatigue as your team manually correlates incidents across screens.
                </p>
              </div>

              <div className="relative h-[270px] w-full bg-[#080808] rounded-xl border border-white/5 flex items-center justify-center overflow-hidden lg:mt-16">
                <div className="absolute z-20 flex flex-col items-center">
                  <div className="bg-red-500/20 p-2 rounded-full animate-pulse">
                    <span className="material-symbols-outlined text-red-500 text-5xl">
                      sentiment_stressed
                    </span>
                  </div>
                </div>

                <div className="absolute left-10 top-10 silo-island border-t-2 border-blue-500/50">
                  <span className="material-symbols-outlined text-blue-400 mb-2">
                    description
                  </span>
                  <span className="text-xs font-mono text-gray-400">Log Silo</span>
                </div>

                <div className="absolute right-10 top-10 silo-island border-t-2 border-green-500/50">
                  <span className="material-symbols-outlined text-green-400 mb-2">
                    bar_chart
                  </span>
                  <span className="text-xs font-mono text-gray-400">Metric Silo</span>
                </div>

                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 silo-island border-t-2 border-purple-500/50">
                  <span className="material-symbols-outlined text-purple-400 mb-2">
                    timeline
                  </span>
                  <span className="text-xs font-mono text-gray-400">Trace Silo</span>
                </div>

                <div className="jagged-bolt top-[35%] left-[28%] w-[15%] rotate-12" />
                <div className="jagged-bolt top-[35%] right-[28%] w-[15%] -rotate-12" />
                <div className="jagged-bolt bottom-[35%] left-[45%] h-[15%] w-0.5" />
              </div>
            </div>
          </section>

          <section className="mb-20 scroll-mt-24 px-4 sm:px-0" id="intelligent-solution">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-slate-900 dark:text-white">
                The Solution:{" "}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-orange-500">
                  Intelligent Ingestion to Unified Timeline
                </span>
              </h2>

              <p className="text-slate-600 dark:text-slate-400 mx-auto">
                From chaos to clarity in three automated steps.
              </p>
            </div>

            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
              <div
                className="hidden md:block absolute top-1/2 left-0 w-full h-0.5
                  bg-gradient-to-r
                  from-transparent
                  via-orange-400/40
                  dark:via-orange-500/40
                  to-transparent
                  -z-10 -translate-y-1/2"
              />

              {[
                {
                  icon: "filter_alt",
                  color: "netraa-cyan",
                  title: "1. Agnostic Ingestion",
                  strong: "We connect to everything.",
                  desc: "Vendor-agnostic intake from any monitoring source, regardless of format or protocol.",
                },
                {
                  icon: "psychology",
                  color: "netraa-purple",
                  title: "2. AI Normalization",
                  strong: "Translating the noise.",
                  desc: "Our AI maps disparate alert schemas into a common language, enriching data on the fly.",
                },
                {
                  icon: "timeline",
                  color: "netraa-orange",
                  title: "3. Unified Correlation",
                  strong: "Connecting the dots.",
                  desc: "Disconnected alerts are merged into contextualized incidents, reducing noise by up to 90%.",
                },
              ].map((card, i) => (
                <div
                  key={i}
                  className="relative p-6 rounded-xl flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm dark:shadow-lg"
                >
                  <div
                    className={`
                      w-16 h-16 rounded-full
                      ${cardColorMap[card.color].badge}
                      flex items-center justify-center mb-4
                      transition-transform
                    `}
                  >
                    <span className={`material-symbols-outlined ${cardColorMap[card.color].icon} text-3xl`}>
                      {card.icon}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                    {card.title}
                  </h3>

                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                    <strong className="block mb-1 text-slate-900 dark:text-white">
                      {card.strong}
                    </strong>
                    {card.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      <style
        dangerouslySetInnerHTML={{
          __html: `
            @layer base {
                body {
                    @apply font-display text-text-primary bg-bg-main selection:bg-netraa-orange selection:text-white transition-colors duration-300;
                }
                .dark body {
                    @apply bg-[#050505] text-[#F3F4F6];
                }
                body:not(.dark) {
                    @apply bg-[#F9FAFB] text-[#111827];
                }
                h1, h2, h3, h4 {
                    @apply tracking-tight font-display;
                }
            }

            .glass-card {
                @apply bg-glass-gradient backdrop-blur-xl border border-white/10 rounded-2xl shadow-glass transition-all duration-300;
            }
            .dark .glass-card {
                @apply bg-white/5 border-white/10;
            }
            .dark .glass-card:hover {
                @apply border-white/20 bg-white/10;
            }

            html:not(.dark) .glass-card {
                @apply bg-white border-gray-200 shadow-lg;
            }
            html:not(.dark) .glass-card:hover {
                @apply border-gray-300 bg-gray-50;
            }

            .neon-text-orange {
                text-shadow: 0 0 15px rgba(255, 145, 77, 0.6);
            }
            .neon-text-cyan {
                text-shadow: 0 0 15px rgba(0, 229, 255, 0.6);
            }
            .neon-text-purple {
                text-shadow: 0 0 15px rgba(124, 77, 255, 0.6);
            }

            .sidebar-scroll::-webkit-scrollbar {
                width: 5px;
            }
            .sidebar-scroll::-webkit-scrollbar-track {
                background: transparent;
            }
            .sidebar-scroll::-webkit-scrollbar-thumb {
                background: #1E1E22; border-radius:9999px;
            }
            .sidebar-scroll::-webkit-scrollbar-thumb:hover {
                background: #FF5722;
            }

            .orbit-icon {
                position: absolute;
                padding: .5rem;
                border-radius: .75rem;
                background: #0F0F11;
                border: 1px solid rgba(255,255,255,0.1);
                box-shadow: 0 8px 32px rgba(0,0,0,0.5);
                display:flex;
                align-items:center;
                justify-content:center;
                font-size:.75rem;
                font-weight:700;
                width:2.5rem;
                height:2.5rem;
            }
            .silo-island {
                display:flex;
                flex-direction:column;
                align-items:center;
                justify-content:center;
                width:6rem;
                height:8rem;
                background: linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01));
                backdrop-filter: blur(12px);
                border-radius:1rem;
                border:1px solid rgba(255,255,255,0.05);
            }
            .jagged-bolt {
                position:absolute;
                height:2px;
                background: #F59E0B;
                box-shadow:0 0 8px rgba(255,255,0,0.8);
            }

            html:not(.dark) header {
                background: rgba(255,255,255,0.8);
                border-bottom:1px solid #E5E7EB;
                color:#111827;
            }
            html:not(.dark) .text-text-secondary { color:#6B7280; }
            html:not(.dark) .border-border-dark { border-color:#E5E7EB; }
            html:not(.dark) .bg-bg-card { background:#FFFFFF; }
            html:not(.dark) #main-content { background:#FFFFFF; }

            @keyframes float { 0%,100%{ transform:translateY(0);} 50%{ transform:translateY(-10px);} }
            @keyframes beam { 0%{ opacity:.3; width:90%; } 50%{ opacity:1; width:100%; } 100%{ opacity:.3; width:90%; } }
          `,
        }}
      />
    </div>
  );
};